/**
 * What a typed search text is: a listing number, a reg plate, or plain text
 * (P25009-512).
 *
 * CommonJS, because the API server reads this too: Dina annonser classifies in
 * the browser and the lookup routes classify again on the server, and the two
 * must never disagree about what counts as a number or a plate. The plate
 * cleaning is also the one rule server/api-util/plateFormat.js uses.
 *
 * Pure: strings in, plain values out. Nothing here logs — the input can be a
 * reg plate, which is personal data.
 */

/* A listing number is "WP-" + the first two characters of the seller's display
 * name, upper case + a five-digit counter (EditListingPage.duck.js
 * saveLastTireIdToMetaData). Nearly all are two letters; Test also holds
 * "WP-T 00001" and "WP-T00001", from display names with a space or of one
 * character (C-5, 2026-10-21). Those odd ones are matched only when typed
 * with the WP- prefix, exactly as printed. */
const TIRE_ID_STANDARD = /^(?:WP-?)?([A-ZÅÄÖ]{2})(\d{5})$/;
const TIRE_ID_ODD_PREFIX = /^WP-([^-]{1,2})(\d{5})$/;

/* 2–7 letters or digits after cleaning: Swedish plates are six, and the range
 * lets short and foreign plates through. A word like "VOLVO" fits too, which is
 * why a plate lookup with no match falls back to the text search (BR-6). */
const PLATE_PATTERN = /^[A-ZÅÄÖ0-9]{2,7}$/;

const asText = input => (input === undefined || input === null ? '' : String(input));

/**
 * The cleaning every plate goes through, valid or not: spaces and dashes out,
 * upper case. "abc 123", "ABC-123" and "abc123" all become "ABC123".
 */
const cleanPlate = input => asText(input).replace(/[\s-]/g, '').toUpperCase();

/**
 * The listing number in its stored form, or null when the text is not one.
 * Case, missing "WP-" and outer spaces do not matter (BR-5).
 *
 * @param {*} input
 * @returns {string|null} e.g. "WP-CM03367"
 */
const normaliseTireId = input => {
  const text = asText(input)
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
  if (!text) return null;
  const standard = text.match(TIRE_ID_STANDARD);
  if (standard) return `WP-${standard[1]}${standard[2]}`;
  const odd = text.match(TIRE_ID_ODD_PREFIX);
  return odd ? `WP-${odd[1]}${odd[2]}` : null;
};

/**
 * The plate in its compared form, or null when the text cannot be a plate.
 *
 * @param {*} input
 * @returns {string|null} e.g. "ABC123"
 */
const normalisePlate = input => {
  const plate = cleanPlate(input);
  return PLATE_PATTERN.test(plate) ? plate : null;
};

/**
 * The order of checks from BR-11: a listing number first, then a plate, else
 * plain text. "CM03367" is seven characters and would pass as a plate, but it
 * is a listing number (A7).
 *
 * @param {*} input
 * @returns {{ type: 'tireId'|'plate', value: string } | { type: 'text' }}
 */
const classifyLookup = input => {
  const tireId = normaliseTireId(input);
  if (tireId) return { type: 'tireId', value: tireId };
  const plate = normalisePlate(input);
  if (plate) return { type: 'plate', value: plate };
  return { type: 'text' };
};

/** The plate a listing carries, under either of its two field names. */
const listingPlateOf = (publicData = {}) => {
  const raw = publicData?.carRegistrationNumber || publicData?.regPlate;
  return raw === undefined || raw === null || String(raw).trim() === '' ? null : raw;
};

module.exports = {
  cleanPlate,
  normaliseTireId,
  normalisePlate,
  classifyLookup,
  listingPlateOf,
};
